import { navigate } from "gatsby";
import React, { useState } from "react";

import { Image, Link, SvgIcon } from "@global";
import { useHeaderQuery } from "@query";
import { useStore } from "@state/store";
import { isAnathoth, isEveryday } from "@util/constants";
import { useCheckScreenWidth } from "@util/hooks";
import { CartBubble, Container, P, TextButton } from "@util/standard";
import { isSanityMegaMenuLinks } from "@util/types";
import MenuDropDown from "./menuDropdown";
import MobileHeader from "./mobileHeader";
import {
  ExternalButton,
  HeaderContainer,
  HeadWrapper,
  SectionContainer,
  TopContainer,
} from "./shared.styles";

interface Props {
  hasScrolled: boolean;
}

const Header = ({ hasScrolled }: Props) => {
  const headerData = useHeaderQuery();

  if (headerData == null) {
    return <P color="errorRed">Error with headerData</P>;
  }
  const { headerContent } = headerData;
  if (headerContent == null || headerContent.length < 1) {
    return <P color="errorRed">Error with headerContent</P>;
  }

  const content = headerContent[0];
  if (content == null) {
    return <P color="errorRed">Error with content</P>;
  }

  const { externalLinks, logo, menuItemLinks, reverseLogo } = content;

  const [hoverIndex, setHoverIndex] = useState(-1);
  const hovered = hoverIndex !== -1;
  const { shopify, openLoginDrawer, openCartDrawer } = useStore();
  const { isTabletWidth, isMobileWidth, isSmallLaptopWidth, isLaptopWidth } =
    useCheckScreenWidth();
  const desktopLogoWidth = isAnathoth ? 140 : 190;

  const handleAccountClick = () => {
    if (shopify?.customer?.token) {
      navigate("/account");
    } else {
      openLoginDrawer();
    }
  };

  const cartTotal: number = shopify?.cart?.lines?.edges
    ? shopify.cart.lines.edges
      .map(lineItem => lineItem?.node?.quantity ?? 0)
      .reduce((a: number, b: number) => a + b, 0)
    : 0;

  return (
    <HeadWrapper hovered={hovered}>
      {!isSmallLaptopWidth &&
        !isTabletWidth &&
        !isMobileWidth &&
        !isLaptopWidth && (
          <HeaderContainer>
            <SectionContainer justifyContent="flex-start">
              {menuItemLinks?.map((menuItemLink, index) => {
                if (!menuItemLink) return null;

                if (isSanityMegaMenuLinks(menuItemLink)) {
                  return (
                    <MenuDropDown
                      key={menuItemLink._key}
                      data={menuItemLink}
                      hover={hoverIndex === index}
                      setHover={setHoverIndex}
                      hoverIndex={index}
                    />
                  );
                }

                return (
                  <Link
                    {...menuItemLink}
                    key={menuItemLink._key}
                    className={!isEveryday ? "header-link" : undefined}
                    onMouseEnter={() => setHoverIndex(-1)}
                  />
                );
              })}
            </SectionContainer>
            <Container
              minWidth={`${desktopLogoWidth}px`}
              margin="0 23px"
              width={`${desktopLogoWidth}px`}
              justifyContent="center"
            >
              {reverseLogo && (hasScrolled || hovered) ? (
                <Image
                  staticImage={reverseLogo.asset?.url}
                  altText={reverseLogo.altText as string}
                  onClick={() => navigate("/")}
                  width={hasScrolled ? "120px" : "100%"}
                />
              ) : (
                logo && (
                  <Image
                    staticImage={logo.asset?.url}
                    altText={logo.altText as string}
                    onClick={() => navigate("/")}
                    width="100%"
                  />
                )
              )}
            </Container>
            <TopContainer width={`calc(50% - (${desktopLogoWidth}px / 2))`}>
              <SectionContainer
                hasBorderBottom
                justifyContent="flex-end"
                hasScrolled={hasScrolled}
                hideOnScroll
                hovered={hovered}
                height="47px"
              ></SectionContainer>

              <SectionContainer justifyContent="flex-end">
                {externalLinks?.[0] &&
                  <ExternalButton {...externalLinks[0]} />
                }

                <SvgIcon asset="search" onClick={() => navigate("/search")} />
                <Container position="relative">
                  <SvgIcon asset="cart" onClick={openCartDrawer} />
                  {cartTotal > 0 && (
                    <CartBubble position="absolute">
                      <span>{cartTotal}</span>
                    </CartBubble>
                  )}
                </Container>

                <TextButton
                  onClick={handleAccountClick}
                  bold
                  fontSizeLaptopDown={14}
                >
                  Account
                </TextButton>
              </SectionContainer>
            </TopContainer>
          </HeaderContainer>
        )}
      {(isSmallLaptopWidth ||
        isTabletWidth ||
        isMobileWidth ||
        isLaptopWidth) && (
          <MobileHeader
            logo={logo}
            reverseLogo={reverseLogo}
            hasScrolled={hasScrolled}
            menuItemLinks={menuItemLinks}
            externalLinks={externalLinks}
            drawer={{
              openCart: openCartDrawer,
              openAccount: handleAccountClick,
            }}
            cartTotal={cartTotal}
          />
        )}
    </HeadWrapper>
  );
};

export default Header;
